/**
 * 資料 新規作成ダイアログ — 用途を選んで初期セクション構成を投入。
 * 「空のテンプレートで作成」も可能。
 */
import type { DocumentUseCase } from '@goenhub/shared';
import { Boxes, Layers, Loader2, ServerCog, Sparkles } from 'lucide-react';
import { useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import { useUpsertDocument } from '@/hooks/useDocuments';

import {
  USE_CASE_DESCRIPTION,
  USE_CASE_LABEL,
  buildTemplateSections,
} from '../sectionRegistry';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (id: string) => void;
}

const USE_CASES: Array<{
  value: DocumentUseCase;
  icon: typeof Boxes;
}> = [
  { value: 'physical_product', icon: Boxes },
  { value: 'service', icon: Sparkles },
  { value: 'b2b_lease', icon: Layers },
  { value: 'platform', icon: ServerCog },
];

export function DocumentCreateDialog({ open, onOpenChange, onCreated }: Props) {
  const upsert = useUpsertDocument();
  const [creating, setCreating] = useState<DocumentUseCase | 'empty' | null>(
    null,
  );

  const handleCreate = async (useCase: DocumentUseCase | null) => {
    setCreating(useCase ?? 'empty');
    try {
      const slug = `untitled-${Math.random().toString(36).slice(2, 8)}`;
      const sections = useCase ? buildTemplateSections(useCase) : [];
      const r = await upsert.mutateAsync({
        title: useCase ? `${USE_CASE_LABEL[useCase]} 資料` : '無題の資料',
        slug,
        status: 'draft',
        sections,
      });
      onCreated(r.id);
      onOpenChange(false);
    } catch (e) {
      toast.error(`作成失敗: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setCreating(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>新規資料を作成</DialogTitle>
          <DialogDescription>
            用途を選ぶと、最適なセクション構成が初期投入されます。後から自由に編集・追加・削除できます。
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {USE_CASES.map(({ value, icon: Icon }) => {
            const isLoading = creating === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => void handleCreate(value)}
                disabled={creating !== null}
                className="text-left p-4 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)] transition-colors disabled:opacity-50"
              >
                <div className="flex items-center gap-2 mb-2">
                  {isLoading ? (
                    <Loader2 className="size-5 animate-spin text-[var(--accent)]" />
                  ) : (
                    <Icon className="size-5 text-[var(--accent)]" />
                  )}
                  <div className="font-semibold text-sm">
                    {USE_CASE_LABEL[value]}
                  </div>
                </div>
                <div className="text-[11px] text-[var(--text-muted)]">
                  {USE_CASE_DESCRIPTION[value]}
                </div>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => void handleCreate(null)}
          disabled={creating !== null}
          className="text-[11px] text-[var(--accent)] hover:underline mt-2 self-start"
        >
          {creating === 'empty' ? '作成中…' : '空のテンプレートで作成'}
        </button>
      </DialogContent>
    </Dialog>
  );
}
